<template>
  <div class="totp-layout">
    <Sidebar />
    <div class="main-content">
      <TopHeader />
      <main class="page-content">
    <div class="totp-page">
      <div class="page-header">
        <h1 class="page-title">Two-Factor Authentication</h1>
        <p class="page-subtitle">Protect your account with an authenticator app</p>
      </div>

      <!-- Loading -->
      <div v-if="loading" class="status-card">
        <div class="spinner-wrap"><span class="spinner"></span> Loading...</div>
      </div>

      <!-- Enabled state -->
      <div v-else-if="totpEnabled" class="status-card enabled-card">
        <div class="status-icon enabled-icon">
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
            <polyline points="9 12 11 14 15 10"/>
          </svg>
        </div>
        <div class="status-info">
          <h2 class="status-title">Two-factor authentication is ON</h2>
          <p class="status-desc">
            Every login requires a code from your authenticator app.
            <span v-if="recoveryCodesRemaining > 0">
              You have <strong>{{ recoveryCodesRemaining }}</strong> recovery code{{ recoveryCodesRemaining !== 1 ? 's' : '' }} remaining.
            </span>
            <span v-else class="no-codes-warning">You have no recovery codes left — consider disabling and re-enabling TOTP to generate new ones.</span>
          </p>
        </div>

        <!-- Disable form -->
        <div v-if="!showDisable" class="action-row">
          <button class="btn-danger" @click="showDisable = true">Disable Two-Factor Auth</button>
        </div>
        <div v-else class="disable-form">
          <h3 class="section-label">Confirm to disable</h3>
          <div class="form-group">
            <label class="form-label">Current password</label>
            <input v-model="disableForm.password" type="password" class="form-input" placeholder="Enter your password" />
          </div>
          <div class="form-group">
            <label class="form-label">Authenticator code</label>
            <input v-model="disableForm.code" type="text" inputmode="numeric" maxlength="6" class="form-input totp-input" placeholder="000000" />
          </div>
          <div v-if="disableError" class="error-msg">{{ disableError }}</div>
          <div class="btn-row">
            <button class="btn-secondary" @click="showDisable = false">Cancel</button>
            <button class="btn-danger" :disabled="disabling" @click="handleDisable">
              <span v-if="!disabling">Disable</span>
              <span v-else><span class="spinner sm"></span> Disabling...</span>
            </button>
          </div>
        </div>
      </div>

      <!-- Disabled state: show setup steps -->
      <div v-else class="setup-flow">

        <!-- Step 1: Start setup -->
        <div v-if="setupStep === 0" class="status-card">
          <div class="status-icon disabled-icon">
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
            </svg>
          </div>
          <div class="status-info">
            <h2 class="status-title">Two-factor authentication is OFF</h2>
            <p class="status-desc">Add an extra layer of security. After enabling, every login will require a 6-digit code from your authenticator app.</p>
          </div>
          <div class="action-row">
            <button class="btn-primary" :disabled="startLoading" @click="startSetup">
              <span v-if="!startLoading">Setup Authenticator</span>
              <span v-else><span class="spinner sm"></span> Loading...</span>
            </button>
          </div>
          <div v-if="setupError" class="error-msg">{{ setupError }}</div>
        </div>

        <!-- Step 2: Scan QR code -->
        <div v-if="setupStep === 1" class="status-card">
          <h2 class="step-title">Step 1 — Scan the QR code</h2>
          <p class="step-desc">Open <strong>Microsoft Authenticator</strong> (or any TOTP app), tap <strong>+</strong> → <strong>Other account</strong>, then scan this QR code.</p>

          <div class="qr-wrap">
            <img v-if="qrDataUrl" :src="qrDataUrl" alt="TOTP QR Code" class="qr-img" />
            <div v-else class="qr-placeholder"><span class="spinner"></span></div>
          </div>

          <details class="manual-entry">
            <summary>Can't scan? Enter the key manually</summary>
            <div class="manual-key">
              <span class="key-label">Account:</span> <span class="key-val">{{ qrLabel }}</span><br/>
              <span class="key-label">Key:</span> <span class="key-val secret-key">{{ qrSecret }}</span>
            </div>
          </details>

          <button class="btn-primary" @click="setupStep = 2">I've scanned it &rarr;</button>
        </div>

        <!-- Step 3: Confirm code -->
        <div v-if="setupStep === 2" class="status-card">
          <h2 class="step-title">Step 2 — Confirm it works</h2>
          <p class="step-desc">Enter the 6-digit code currently shown in your authenticator app.</p>

          <div class="form-group">
            <input
              v-model="confirmCode"
              type="text"
              inputmode="numeric"
              maxlength="6"
              class="form-input totp-input"
              placeholder="000000"
              @keyup.enter="handleEnable"
            />
          </div>
          <div v-if="enableError" class="error-msg">{{ enableError }}</div>

          <div class="btn-row">
            <button class="btn-secondary" @click="setupStep = 1">&larr; Back</button>
            <button class="btn-primary" :disabled="enabling" @click="handleEnable">
              <span v-if="!enabling">Enable</span>
              <span v-else><span class="spinner sm"></span> Enabling...</span>
            </button>
          </div>
        </div>

        <!-- Step 4: Recovery codes -->
        <div v-if="setupStep === 3" class="status-card recovery-card">
          <div class="status-icon enabled-icon">
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
              <polyline points="9 12 11 14 15 10"/>
            </svg>
          </div>
          <h2 class="step-title success-title">Two-factor authentication enabled!</h2>
          <p class="step-desc"><strong>Save your recovery codes.</strong> If you ever lose access to your authenticator app, you can use one of these codes to log in. Each code works only once.</p>

          <div class="recovery-codes-grid">
            <div v-for="code in recoveryCodes" :key="code" class="recovery-code">{{ code }}</div>
          </div>

          <div class="recovery-actions">
            <button class="btn-secondary" @click="copyRecoveryCodes">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/>
                <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
              </svg>
              {{ copied ? 'Copied!' : 'Copy codes' }}
            </button>
          </div>

          <p class="recovery-warning">These codes will not be shown again. Store them somewhere safe.</p>
          <button class="btn-primary" @click="finishSetup">Done</button>
        </div>
      </div>
    </div>
      </main>
    </div>
  </div>
</template>

<script>
import Sidebar from '@/components/Sidebar.vue'
import TopHeader from '@/components/TopHeader.vue'
import { AuthService } from '@/services/auth'
import QRCode from 'qrcode'

const API = 'https://api.quali-d.com'

export default {
  name: 'TotpSettingsView',
  components: { Sidebar, TopHeader },
  data () {
    return {
      loading: true,
      totpEnabled: false,
      recoveryCodesRemaining: 0,
      // Setup flow
      setupStep: 0,
      startLoading: false,
      setupError: '',
      qrSecret: '',
      qrLabel: '',
      qrDataUrl: '',
      confirmCode: '',
      enableError: '',
      enabling: false,
      recoveryCodes: [],
      copied: false,
      // Disable flow
      showDisable: false,
      disableForm: { password: '', code: '' },
      disableError: '',
      disabling: false
    }
  },
  async mounted () {
    await this.loadStatus()
  },
  methods: {
    async loadStatus () {
      this.loading = true
      try {
        const res = await AuthService.makeAuthenticatedRequest(`${API}/totp/status`)
        const data = await res.json()
        if (data.success) {
          this.totpEnabled = data.totp_enabled
          this.recoveryCodesRemaining = data.recovery_codes_remaining
        }
      } catch (e) {
        console.error('Failed to load TOTP status', e)
      } finally {
        this.loading = false
      }
    },

    async startSetup () {
      this.startLoading = true
      this.setupError = ''
      try {
        const res = await AuthService.makeAuthenticatedRequest(`${API}/totp/setup`)
        const data = await res.json()
        if (!data.success) throw new Error(data.message)
        this.qrSecret = data.secret
        this.qrLabel = data.label
        this.qrDataUrl = await QRCode.toDataURL(data.qr_uri, { width: 220, margin: 2, color: { dark: '#1f2937', light: '#ffffff' } })
        this.setupStep = 1
      } catch (e) {
        this.setupError = e.message || 'Failed to start setup'
      } finally {
        this.startLoading = false
      }
    },

    async handleEnable () {
      this.enableError = ''
      if (!this.confirmCode) return
      this.enabling = true
      try {
        const res = await AuthService.makeAuthenticatedRequest(`${API}/totp/enable`, {
          method: 'POST',
          body: JSON.stringify({ secret: this.qrSecret, code: this.confirmCode })
        })
        const data = await res.json()
        if (!data.success) throw new Error(data.message)
        this.recoveryCodes = data.recovery_codes
        this.setupStep = 3
      } catch (e) {
        this.enableError = e.message || 'Failed to enable TOTP'
      } finally {
        this.enabling = false
      }
    },

    async handleDisable () {
      this.disableError = ''
      this.disabling = true
      try {
        const res = await AuthService.makeAuthenticatedRequest(`${API}/totp/disable`, {
          method: 'POST',
          body: JSON.stringify(this.disableForm)
        })
        const data = await res.json()
        if (!data.success) throw new Error(data.message)
        this.totpEnabled = false
        this.showDisable = false
        this.disableForm = { password: '', code: '' }
        this.setupStep = 0
      } catch (e) {
        this.disableError = e.message || 'Failed to disable TOTP'
      } finally {
        this.disabling = false
      }
    },

    copyRecoveryCodes () {
      navigator.clipboard.writeText(this.recoveryCodes.join('\n'))
      this.copied = true
      setTimeout(() => { this.copied = false }, 2000)
    },

    finishSetup () {
      this.totpEnabled = true
      this.recoveryCodesRemaining = this.recoveryCodes.length
      this.setupStep = 0
      this.recoveryCodes = []
    }
  }
}
</script>

<style scoped>
.totp-layout {
  display: flex;
  min-height: 100vh;
  background: #f9fafb;
}

.main-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-width: 0;
  margin-left: var(--sidebar-width, 280px);
  transition: margin-left 0.3s ease;
}

.page-content {
  flex: 1;
  padding: 24px;
  overflow-y: auto;
}

.totp-page {
  max-width: 620px;
  margin: 0 auto;
  padding: 32px 24px;
}

.page-header {
  margin-bottom: 32px;
}

.page-title {
  font-size: 24px;
  font-weight: 700;
  color: #1f2937;
  margin: 0 0 6px;
}

.page-subtitle {
  color: #6b7280;
  margin: 0;
  font-size: 15px;
}

.status-card {
  background: white;
  border-radius: 16px;
  padding: 32px;
  box-shadow: 0 1px 8px rgba(0,0,0,0.08);
  margin-bottom: 24px;
}

.enabled-card {
  border-left: 4px solid #10b981;
}

.status-icon {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 16px;
}

.enabled-icon {
  background: #d1fae5;
  color: #059669;
}

.disabled-icon {
  background: #f3f4f6;
  color: #9ca3af;
}

.status-title {
  font-size: 18px;
  font-weight: 700;
  color: #1f2937;
  margin: 0 0 8px;
}

.status-desc {
  color: #6b7280;
  font-size: 14px;
  margin: 0;
  line-height: 1.6;
}

.no-codes-warning {
  color: #ef4444;
}

.action-row {
  margin-top: 24px;
}

.btn-primary {
  background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 10px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  transition: all 0.2s;
  width: 100%;
  justify-content: center;
}

.btn-primary:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 6px 20px rgba(59,130,246,0.3);
}

.btn-primary:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.btn-secondary {
  background: #f3f4f6;
  color: #374151;
  border: none;
  padding: 12px 24px;
  border-radius: 10px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  transition: background 0.2s;
}

.btn-secondary:hover {
  background: #e5e7eb;
}

.btn-danger {
  background: #ef4444;
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 10px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  transition: all 0.2s;
}

.btn-danger:hover:not(:disabled) {
  background: #dc2626;
}

.btn-danger:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.btn-row {
  display: flex;
  gap: 12px;
  margin-top: 16px;
}

.step-title {
  font-size: 18px;
  font-weight: 700;
  color: #1f2937;
  margin: 0 0 10px;
}

.success-title {
  color: #059669;
}

.step-desc {
  color: #6b7280;
  font-size: 14px;
  margin: 0 0 24px;
  line-height: 1.6;
}

.qr-wrap {
  display: flex;
  justify-content: center;
  margin: 24px 0;
}

.qr-img {
  border: 3px solid #e5e7eb;
  border-radius: 12px;
  padding: 8px;
}

.qr-placeholder {
  width: 220px;
  height: 220px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f9fafb;
  border-radius: 12px;
}

.manual-entry {
  margin-bottom: 24px;
  font-size: 14px;
}

.manual-entry summary {
  cursor: pointer;
  color: #3b82f6;
  font-weight: 500;
  padding: 6px 0;
}

.manual-key {
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 12px 16px;
  margin-top: 8px;
  line-height: 1.8;
  font-size: 13px;
}

.key-label {
  color: #6b7280;
}

.key-val {
  color: #1f2937;
  font-weight: 600;
}

.secret-key {
  font-family: monospace;
  word-break: break-all;
  letter-spacing: 2px;
}

.form-group {
  margin-bottom: 16px;
}

.form-label {
  display: block;
  font-size: 13px;
  font-weight: 600;
  color: #374151;
  margin-bottom: 6px;
}

.form-input {
  width: 100%;
  padding: 12px 16px;
  border: 2px solid #e5e7eb;
  border-radius: 10px;
  font-size: 15px;
  color: #1f2937;
  outline: none;
  transition: border-color 0.2s;
  box-sizing: border-box;
}

.form-input:focus {
  border-color: #3b82f6;
}

.totp-input {
  text-align: center;
  font-size: 28px;
  font-weight: 700;
  letter-spacing: 10px;
}

.error-msg {
  background: #fef2f2;
  border: 1px solid #fecaca;
  color: #dc2626;
  padding: 10px 14px;
  border-radius: 8px;
  font-size: 13px;
  margin-bottom: 12px;
}

.section-label {
  font-size: 14px;
  font-weight: 700;
  color: #374151;
  margin: 0 0 16px;
}

.disable-form {
  margin-top: 24px;
  padding-top: 24px;
  border-top: 1px solid #f3f4f6;
}

.recovery-codes-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
  margin: 16px 0;
}

.recovery-code {
  background: #f3f4f6;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 10px 12px;
  font-family: monospace;
  font-size: 15px;
  font-weight: 600;
  text-align: center;
  color: #1f2937;
  letter-spacing: 2px;
}

.recovery-actions {
  margin-bottom: 12px;
}

.recovery-warning {
  color: #f59e0b;
  font-size: 13px;
  font-weight: 600;
  margin: 12px 0 20px;
}

.recovery-card {
  border-left: 4px solid #10b981;
}

.status-info {
  flex: 1;
}

.spinner-wrap {
  display: flex;
  align-items: center;
  gap: 10px;
  color: #6b7280;
  font-size: 14px;
}

.spinner {
  display: inline-block;
  width: 20px;
  height: 20px;
  border: 2px solid rgba(0,0,0,0.1);
  border-top-color: #3b82f6;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

.spinner.sm {
  width: 14px;
  height: 14px;
  border-width: 2px;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}
</style>
